// src/pages/ProductionReports.jsx
// Reporte específico para deptos con slaMode='materialType' (hoy: Comunicación
// y Desarrollo). Reemplaza el "promedio de días" tradicional — irrelevante en
// piezas con tiempos largos — por KPIs accionables: entregas a tiempo, horas
// estimadas invertidas y desglose por tipo de pieza.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import api from '../services/api';

// ── Design tokens (alineados con Reports.jsx) ────────────────────────────────
const CARD  = { background: 'var(--c-card)', borderRadius: 12, border: '1px solid var(--c-border)', boxShadow: '0 1px 4px rgba(0,0,0,.06)' };
const LABEL = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--c-text-4)', margin: 0 };

function KpiCard({ title, value, color = '#6b7280', subtitle }) {
    return (
        <div style={{ ...CARD, borderTop: `4px solid ${color}`, padding: '16px 20px', height: '100%' }}>
            <p style={LABEL}>{title}</p>
            <p style={{ fontSize: 28, fontWeight: 800, color: 'var(--c-text-1)', margin: '6px 0 0', lineHeight: 1 }}>{value}</p>
            {subtitle && <p style={{ fontSize: 11, color: 'var(--c-text-4)', margin: '4px 0 0' }}>{subtitle}</p>}
        </div>
    );
}

function fmtDate(d) {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleDateString('es-MX', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    } catch { return '—'; }
}

function fmtDateInput(d) {
    if (!d) return '';
    try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
}

function fmtHours(h) {
    if (h == null) return '—';
    if (h === 0) return '0 h';
    if (h < 1) return `${h.toFixed(1)} h`;
    return `${Math.round(h)} h`;
}

export default function ProductionReports() {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr]         = useState('');

    // Filtros (defaults se completan con el mes actual al primer fetch)
    const today = useMemo(() => new Date(), []);
    const firstOfMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
    const lastOfMonth  = useMemo(() => new Date(today.getFullYear(), today.getMonth() + 1, 0), [today]);

    const [deptId, setDeptId]   = useState('');
    const [fromDate, setFrom]   = useState(fmtDateInput(firstOfMonth));
    const [toDate, setTo]       = useState(fmtDateInput(lastOfMonth));

    const load = useCallback(() => {
        setLoading(true);
        setErr('');
        const params = {};
        if (deptId)   params.deptId = deptId;
        if (fromDate) params.from   = fromDate;
        if (toDate)   params.to     = toDate;
        api.get('/tickets/production-reports', { params })
            .then(res => {
                setData(res.data);
                // Si no había deptId seleccionado, persistir el que vino del server
                if (!deptId && res.data.selectedDeptId) setDeptId(String(res.data.selectedDeptId));
            })
            .catch(e => setErr(e.response?.data?.error || e.message))
            .finally(() => setLoading(false));
    }, [deptId, fromDate, toDate]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    const noDepts = data && (!data.availableDepts || data.availableDepts.length === 0);

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
            <div style={{ marginBottom: 18 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: 'var(--c-text-1)' }}>
                    Reportes de Producción
                </h1>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--c-text-3)' }}>
                    Entregas a tiempo y horas invertidas para deptos de producción (piezas por tipo)
                </p>
            </div>

            {/* Filtros */}
            <div style={{ ...CARD, padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                    <p style={LABEL}>Depto</p>
                    <select
                        className="form-select form-select-sm"
                        value={deptId}
                        onChange={e => setDeptId(e.target.value)}
                        style={{ minWidth: 220 }}
                        disabled={noDepts}
                    >
                        {(data?.availableDepts || []).map(d => (
                            <option key={d._id} value={d._id}>{d.name}</option>
                        ))}
                        {noDepts && <option value="">(sin deptos de producción)</option>}
                    </select>
                </div>

                <div>
                    <p style={LABEL}>Desde</p>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={fromDate}
                        onChange={e => setFrom(e.target.value)}
                    />
                </div>

                <div>
                    <p style={LABEL}>Hasta</p>
                    <input
                        type="date"
                        className="form-control form-control-sm"
                        value={toDate}
                        onChange={e => setTo(e.target.value)}
                    />
                </div>

                <div style={{ flex: 1, textAlign: 'right', fontSize: 12, color: 'var(--c-text-4)' }}>
                    {loading && <Spinner animation="border" size="sm" />}
                </div>
            </div>

            {err && <div className="alert alert-danger" style={{ fontSize: 13 }}>{err}</div>}

            {noDepts ? (
                <div style={{ ...CARD, padding: '40px 20px', textAlign: 'center', color: 'var(--c-text-3)' }}>
                    No tienes deptos de producción (slaMode='materialType') en tu scope.
                </div>
            ) : data && (
                <>
                    {/* Fila 1: KPIs de entregas */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
                        <KpiCard
                            title="Total entregas"
                            value={data.totalDeliveries}
                            color="#2563eb"
                            subtitle="cerradas en el período"
                        />
                        <KpiCard
                            title="A tiempo"
                            value={data.onTime}
                            color="#15803d"
                            subtitle="closedAt ≤ dueAt"
                        />
                        <KpiCard
                            title="Fuera de tiempo"
                            value={data.late}
                            color={data.late > 0 ? '#dc2626' : '#6b7280'}
                            subtitle="closedAt > dueAt"
                        />
                        <KpiCard
                            title="% on-time"
                            value={data.onTimePercent != null ? `${data.onTimePercent}%` : '—'}
                            color="#8b5cf6"
                            subtitle={`${data.onTime + data.late} evaluados`}
                        />
                        <KpiCard
                            title="Pendientes"
                            value={data.pending}
                            color="#d97706"
                            subtitle="actualmente sin cerrar"
                        />
                    </div>

                    {/* Fila 2: horas + materiales */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 2fr', gap: 12, marginBottom: 16 }}>
                        <KpiCard
                            title="Horas invertidas"
                            value={fmtHours(data.hoursInvested)}
                            color="#0891b2"
                            subtitle="estimadas (Σ count × slaHours)"
                        />
                        <div style={{ ...CARD, padding: '14px 18px' }}>
                            <p style={LABEL}>Materiales producidos</p>
                            {data.byMaterial.length === 0 ? (
                                <p style={{ fontSize: 13, color: 'var(--c-text-4)', margin: '8px 0 0' }}>
                                    Sin materiales registrados en el período.
                                </p>
                            ) : (
                                <table className="table table-sm" style={{ marginTop: 8, marginBottom: 0 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Tipo</th>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)', textAlign: 'right' }}>Cantidad</th>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)', textAlign: 'right' }}>Horas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.byMaterial.map(m => (
                                            <tr key={m.type}>
                                                <td style={{ fontWeight: 600 }}>{m.type}</td>
                                                <td style={{ textAlign: 'right' }}>{m.count}</td>
                                                <td style={{ textAlign: 'right' }}>{fmtHours(m.hours)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Tickets fuera de tiempo */}
                    <div style={{ ...CARD, padding: '14px 18px' }}>
                        <p style={LABEL}>Tickets fuera de tiempo</p>
                        {data.lateTickets.length === 0 ? (
                            <p style={{ fontSize: 13, color: 'var(--c-text-4)', margin: '8px 0 0' }}>
                                ✓ Ninguna entrega fuera de tiempo en este período.
                            </p>
                        ) : (
                            <div style={{ overflowX: 'auto', marginTop: 8 }}>
                                <table className="table table-sm table-hover" style={{ marginBottom: 0 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Folio</th>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Título</th>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Vencía</th>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Cerrado</th>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)', textAlign: 'right' }}>Días tarde</th>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Agente</th>
                                            <th style={{ fontSize: 11, color: 'var(--c-text-3)' }}>Piezas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.lateTickets.map(t => (
                                            <tr key={t._id}>
                                                <td>
                                                    <Link to={`/tickets/${t._id}`} style={{ fontWeight: 600, textDecoration: 'none' }}>
                                                        #{t.folio}
                                                    </Link>
                                                </td>
                                                <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {t.title}
                                                </td>
                                                <td style={{ fontSize: 12 }}>{fmtDate(t.dueAt)}</td>
                                                <td style={{ fontSize: 12 }}>{fmtDate(t.closedAt)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>
                                                    {t.daysLate}
                                                </td>
                                                <td style={{ fontSize: 12 }}>{t.assignedTo?.name || '—'}</td>
                                                <td style={{ fontSize: 12 }}>
                                                    {(t.materialItems || []).map((it, i) => (
                                                        <span key={i} style={{ marginRight: 8 }}>
                                                            {it.type} ×{it.count}
                                                        </span>
                                                    )) || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <p style={{ fontSize: 11, color: 'var(--c-text-4)', textAlign: 'right', marginTop: 10 }}>
                        Generado: {data.generatedAt ? new Date(data.generatedAt).toLocaleString('es-MX') : '—'}
                    </p>
                </>
            )}
        </div>
    );
}
